import React from 'react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { projects } from '../../data/portfolio';
import { ArrowUpRight, Github } from 'lucide-react';
import { Link } from 'react-router-dom';

const Projects: React.FC = () => {
  const { ref, inView } = useInView({
    threshold: 0.15,
    triggerOnce: true,
  });

  return (
    <section
      id="projects"
      ref={ref}
      className="bg-transparent py-16 sm:py-20"
      aria-labelledby="projects-heading"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
          <div>
            <h2
              id="projects-heading"
              className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl md:text-3xl dark:text-slate-50"
            >
              Selected projects
            </h2>
            <p className="mt-2 max-w-xl text-sm text-slate-600 sm:text-base dark:text-slate-300">
              A mix of front-end, mobile, and full-stack experiments while
              growing from self-taught beginner to confident junior developer.
            </p>
          </div>
          <Link
            to="/projects"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200/80 bg-white/80 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700 shadow-sm transition hover:border-brand-pink/70 hover:text-slate-900 sm:px-4 sm:py-2 sm:text-xs dark:border-slate-700/80 dark:bg-slate-900/80 dark:text-slate-200 dark:hover:border-brand-pink/70"
          >
            View all projects
            <ArrowUpRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          </Link>
        </div>

        <div className="mt-6 grid gap-4 sm:mt-8 sm:gap-6 md:grid-cols-2">
          {projects.map((project, index) => (
            <motion.article
              key={project.id}
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.06, ease: 'easeOut' }}
              className="glass-card flex flex-col bg-glass-light p-4 sm:p-5 dark:bg-glass-dark"
            >
              <div className="mb-3 flex h-28 min-h-[112px] items-center justify-center overflow-hidden rounded-xl bg-gradient-to-tr from-brand-pink/35 via-brand-purple/30 to-brand-orange/35 sm:mb-4 sm:h-32 sm:min-h-[128px]">
                {project.id === 'flutter-firebase' ? (
                  <img
                    src={(process.env.PUBLIC_URL || '') + '/media/projects/LogoSimplySocialCard.png'}
                    alt={project.imageAlt}
                    className="h-full w-full object-cover object-top transition-transform duration-200 hover:scale-110"
                    loading="eager"
                  />
                ) : project.id === 'react-todo' ? (
                  <img
                    src={(process.env.PUBLIC_URL || '') + '/media/projects/CardAiNoteStudio.png'}
                    alt={project.imageAlt}
                    className="h-full w-full object-cover object-top transition-transform duration-200 hover:scale-110"
                    loading="eager"
                  />
                ) : project.imageUrl ? (
                  <img
                    src={process.env.PUBLIC_URL + project.imageUrl}
                    alt={project.imageAlt}
                    className="max-h-[80%] w-auto max-w-[85%] object-contain object-center p-1 transition-transform duration-300 hover:scale-105 sm:p-1"
                    loading="lazy"
                  />
                ) : null}
              </div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-50">
                {project.title}
              </h3>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                {project.description}
              </p>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                {project.highlight}
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {project.tech.map((tech) => (
                  <span
                    key={tech}
                    className="rounded-full bg-slate-900/5 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-slate-50/5 dark:text-slate-300"
                  >
                    {tech}
                  </span>
                ))}
              </div>
              <div className="mt-4 flex justify-between gap-3 text-xs font-medium text-slate-600 dark:text-slate-300">
                <span className="rounded-full bg-slate-900/90 px-3 py-1 text-[10px] uppercase tracking-wide text-slate-100 dark:bg-white dark:text-slate-900">
                  {project.role}
                </span>
                <div className="flex items-center gap-2">
                  {project.repoUrl && (
                    <a
                      href={project.repoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-[11px] text-slate-100 transition hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                    >
                      <span className="rounded-l-full bg-slate-800 px-1.5 py-1 dark:bg-slate-100">
                        <Github className="h-3 w-3" />
                      </span>
                      <span className="px-2 py-1">Code</span>
                    </a>
                  )}
                  {project.liveUrl && (
                    <a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-full border border-slate-200/80 bg-white/80 px-3 py-1 text-[11px] text-slate-700 transition hover:border-brand-pink/70 hover:text-slate-900 dark:border-slate-700/80 dark:bg-slate-900/80 dark:text-slate-200 dark:hover:border-brand-pink/70"
                    >
                      Live
                      <ArrowUpRight className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;



